import { useCallback, useState } from 'react';
import type { CompanyRow } from '@dru/types';
import { EntityCombobox } from '@dru/ui';
import { page } from '../../../lib/page';

/**
 * The company picker: one field, with the search inside the dropdown
 * (owner, 29 Oct 2026 — the separate search box above a select left a
 * column of empty space and two controls for one choice).
 *
 * Searches the active registry on the server as you type, alphabetical when
 * browsing, and pages beyond the first 25. `list` is what the page already
 * loaded, used only to show the current company's name before any search.
 */
export function CompanySelect({ list, value, onChange, className, label = 'Company' }: {
  list: CompanyRow[]; value: string; onChange: (id: string) => void;
  /** Kept for callers; the picker pages on its own now. */
  truncated?: boolean;
  className?: string;
  label?: string;
}) {
  const [picked, setPicked] = useState<CompanyRow | null>(null);
  const selected = picked?.id === value ? picked : list.find((c) => c.id === value) ?? null;
  const fetchCompanies = useCallback(async (search: string, cursor: string | null) => {
    const res = await page<CompanyRow>(`/tenants?status=ACTIVE&limit=25&search=${encodeURIComponent(search)}`, cursor);
    // Alphabetical: the registry's own order is newest first, which is the
    // order nobody looks a company up in.
    return { ...res, items: [...res.items].sort((a, b) => a.name.localeCompare(b.name, 'en-IN', { sensitivity: 'base' })) };
  }, []);
  return (
    <EntityCombobox<CompanyRow>
      className={className}
      label={label}
      value={selected}
      onChange={(c) => { if (c) { setPicked(c); onChange(c.id); } }}
      fetchOptions={fetchCompanies}
      getKey={(c) => c.id}
      renderOption={(c) => (
        <span className="block">
          <span className="block text-neutral-900">{c.name}</span>
          {c.city && <span className="block text-caption font-normal text-neutral-500">{c.city}</span>}
        </span>
      )}
      renderValue={(c) => c.name}
      placeholder="Choose a company"
      searchPlaceholder="Search companies"
      minChars={1}
      initial={25}
      clearable={false}
      emptyMessage="No active company matches."
    />
  );
}
